import React from 'react';
import { ArrowUpRight, FileCheck2, ShieldCheck, Sparkles, CircleCheck, CircleX, MinusCircle } from 'lucide-react';
import { Button } from './ui/button';
import { sampleResults, heroSteps } from '../mock';

export default function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft gradient blob (under 20% area) */}
      <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-gradient-to-br from-[#D6EBE2] to-[#FAFAF5] blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#E8E5DC] to-[#FAFAF5] blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-20 lg:pb-28 relative">
        {/* Top pill */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0B1F2D]/15 bg-white/60 backdrop-blur-sm text-[11.5px] tracking-[0.12em] uppercase text-[#0B1F2D]/70 fade-up">
          <span className="relative w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-[#1B6F5E]" />
            <span className="live-dot" />
          </span>
          ESG-as-Code® · Seven Frameworks · 90+ Rules
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mt-8">
          {/* Left content */}
          <div className="lg:col-span-7">
            <h1 className="font-display text-[44px] sm:text-[58px] lg:text-[80px] leading-[0.96] text-[#0B1F2D] fade-up">
              We do not score ESG.
              <br />
              <span className="italic text-[#1B6F5E]">We enforce it.</span>
            </h1>

            <p className="mt-7 text-[17px] lg:text-[18.5px] leading-[1.55] text-[#0B1F2D]/70 max-w-[620px] fade-up">
              ESG compliance is a legal problem. We built the tool that treats it like one. Upload your ESG report and receive
              a clause-by-clause compliance result across seven frameworks and 90+ rules. Every outcome maps to a specific
              regulatory clause.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3 fade-up">
              <Button
                asChild
                className="bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-12 px-6 text-[14px] font-medium"
              >
                <a href="#contact" className="inline-flex items-center gap-2">
                  Request Access
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#0B1F2D]/20 hover:border-[#0B1F2D] bg-transparent hover:bg-[#0B1F2D]/[0.03] text-[#0B1F2D] rounded-full h-12 px-6 text-[14px] font-medium"
              >
                <a href="#contact" className="inline-flex items-center gap-2">
                  Book Compliance Briefing
                </a>
              </Button>
            </div>

            {/* Feature pills */}
            <div className="mt-10 flex flex-wrap gap-2 fade-up">
              {[
                'Clause-by-clause compliance',
                'Deterministic rule checks',
                'Audit-ready outputs',
                'Seven live frameworks',
              ].map((t) => (
                <span
                  key={t}
                  className="text-[12.5px] px-3.5 py-2 rounded-full bg-white border border-[#E8E5DC] text-[#0B1F2D]/80"
                >
                  {t}
                </span>
              ))}
            </div>

            <p className="mt-8 text-[12.5px] text-[#0B1F2D]/55 max-w-[520px]">
              ESGine® supports CSRD, SEC Climate, CARB SB253, CARB SB261, ISSB, UK FCA, and SFDR. Seven frameworks. One platform.
            </p>
          </div>

          {/* Right: live preview card */}
          <div className="lg:col-span-5">
            <div className="relative fade-up">
              <div className="absolute -inset-2 rounded-[28px] bg-gradient-to-br from-[#0B1F2D]/5 to-transparent blur-2xl" />
              <div className="relative rounded-[22px] border border-[#E8E5DC] bg-white shadow-[0_30px_80px_-32px_rgba(11,31,45,0.18)] overflow-hidden">
                {/* Card header */}
                <div className="px-6 py-4 border-b border-[#E8E5DC] flex items-center justify-between bg-[#FAFAF5]/60">
                  <div className="flex items-center gap-2">
                    <span className="relative inline-block w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-[#1B6F5E]" />
                      <span className="live-dot" />
                    </span>
                    <span className="text-[11.5px] tracking-[0.12em] uppercase text-[#0B1F2D]/60">
                      Live result · CSRD · 22 rules
                    </span>
                  </div>
                  <span className="font-mono-tight text-[11px] text-[#0B1F2D]/50">v2026.1</span>
                </div>

                {/* Steps */}
                <div className="px-6 py-5 space-y-4 border-b border-[#E8E5DC]">
                  {heroSteps.slice(0, 4).map((s, i) => (
                    <div key={s.n} className="flex gap-4">
                      <div className="shrink-0">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-mono-tight text-[12px] border ${
                            i === 2
                              ? 'bg-[#0B1F2D] text-[#FAFAF5] border-[#0B1F2D]'
                              : 'bg-white text-[#0B1F2D] border-[#E8E5DC]'
                          }`}
                        >
                          {s.n.replace('0', '')}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-[#0B1F2D]">{s.title}</div>
                        <div className="text-[12.5px] text-[#0B1F2D]/60 mt-0.5 leading-[1.5]">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Results preview */}
                <div className="px-6 py-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11.5px] tracking-[0.12em] uppercase text-[#0B1F2D]/50">
                      Example output preview
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-[#0B1F2D]/60">
                      <Sparkles className="w-3 h-3" />
                      <span>Generated</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5">
                    {sampleResults.map((r) => {
                      const ColorMap = {
                        emerald: 'bg-[#E8F3EE] text-[#1B6F5E] border-[#1B6F5E]/20',
                        rose: 'bg-[#FBEAEA] text-[#A4243B] border-[#A4243B]/20',
                        slate: 'bg-[#EEEEEA] text-[#0B1F2D]/70 border-[#0B1F2D]/15',
                      };
                      const Icon = r.color === 'emerald' ? CircleCheck : r.color === 'rose' ? CircleX : MinusCircle;
                      return (
                        <div key={r.code} className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E5DC] bg-[#FAFAF5]/40">
                          <Icon className={`w-4 h-4 mt-0.5 ${r.color === 'emerald' ? 'text-[#1B6F5E]' : r.color === 'rose' ? 'text-[#A4243B]' : 'text-[#0B1F2D]/50'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-semibold tracking-[0.08em] px-2 py-0.5 rounded-full border ${ColorMap[r.color]}`}>
                                {r.status}
                              </span>
                              <span className="font-mono-tight text-[11px] text-[#0B1F2D]/60">{r.code}</span>
                            </div>
                            <div className="mt-1.5 text-[13px] text-[#0B1F2D] font-medium">{r.title}</div>
                            <div className="text-[11.5px] text-[#0B1F2D]/55 mt-0.5">{r.clause}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer chips */}
                <div className="px-6 py-4 border-t border-[#E8E5DC] bg-[#FAFAF5]/50 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[11.5px] text-[#0B1F2D]/60">
                    <span className="inline-flex items-center gap-1.5">
                      <FileCheck2 className="w-3.5 h-3.5" /> PDF
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" /> Audit-ready
                    </span>
                  </div>
                  <a href="#workflow" className="text-[11.5px] font-medium text-[#0B1F2D] inline-flex items-center gap-1 link-underline">
                    View workflow <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
